<script lang="ts">
  /**
   * Text input with suggestions drawn from past notes.
   *
   * A plain `<datalist>` would be less code, but its behaviour is inconsistent
   * across browsers and unusable on mobile Safari — and this is the field that
   * gets typed into most. Keyboard: ↑/↓ to move, Enter/Tab to accept, Esc to
   * dismiss.
   */

  interface Props {
    id?: string;
    value: string;
    suggestions?: string[];
    placeholder?: string;
  }

  let { id, value = $bindable(), suggestions = [], placeholder = 'What did you work on?' }: Props =
    $props();

  let open = $state(false);
  let highlighted = $state(-1);
  let input = $state<HTMLInputElement | null>(null);

  const matches = $derived.by(() => {
    const q = value.trim().toLowerCase();
    if (!q) return suggestions.slice(0, 6);
    return suggestions
      .filter((s) => s.toLowerCase().includes(q) && s.toLowerCase() !== q)
      .slice(0, 6);
  });

  function accept(suggestion: string) {
    value = suggestion;
    open = false;
    highlighted = -1;
    input?.focus();
  }

  function onKeydown(event: KeyboardEvent) {
    if (!open || !matches.length) {
      if (event.key === 'ArrowDown') open = true;
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        highlighted = (highlighted + 1) % matches.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        highlighted = highlighted <= 0 ? matches.length - 1 : highlighted - 1;
        break;
      case 'Enter':
      case 'Tab':
        // Only intercept when something is actually selected, so Enter still
        // submits the form in the normal case.
        if (highlighted >= 0) {
          event.preventDefault();
          accept(matches[highlighted]);
        }
        break;
      case 'Escape':
        open = false;
        highlighted = -1;
        break;
    }
  }
</script>

<div class="wrap">
  <input
    {id}
    {placeholder}
    type="text"
    autocomplete="off"
    bind:this={input}
    bind:value
    oninput={() => {
      open = true;
      highlighted = -1;
    }}
    onfocus={() => (open = true)}
    onblur={() => setTimeout(() => (open = false), 120)}
    onkeydown={onKeydown}
    role="combobox"
    aria-expanded={open && matches.length > 0}
    aria-controls="note-suggestions"
    aria-autocomplete="list"
  />

  {#if open && matches.length}
    <ul id="note-suggestions" role="listbox">
      {#each matches as suggestion, i (suggestion)}
        <li>
          <button
            type="button"
            role="option"
            aria-selected={i === highlighted}
            class:highlighted={i === highlighted}
            onmousedown={() => accept(suggestion)}
            onmouseenter={() => (highlighted = i)}
          >
            {suggestion}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .wrap {
    position: relative;
  }

  ul {
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 0.25rem;
    list-style: none;
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    box-shadow: 0 10px 30px rgb(0 0 0 / 0.35);
    max-height: 15rem;
    overflow-y: auto;
  }

  li button {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    border-radius: 6px;
    min-height: 38px;
    padding: 0.4rem 0.55rem;
    font-size: 0.92rem;
    /* Long notes are common; keep the row readable rather than wrapping. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  li button.highlighted {
    background: var(--accent);
    color: var(--accent-fg);
  }
</style>
